import { Logger } from 'wdc-cube'
import { ValueProperty } from '../tcm_filter_manager'
import {
    JunctionMode,
    KeywordOption,
    MultipleOptionStringKeywordFilterScope,
    MultipleOptionStringKeywordParcelFilterScope
} from '../tcm_scopes'
import { SituacaoCadastral, StringKeywordOperator } from '../tcm_service'
import { FilterPresenter } from './tcm_filter_types'
import { isBlankKeywordOperator } from './tcm_filter_presenter_for_string_multi_select'
import { TcmPresenterForFilterPane } from './tcm_presenter_filter_pane'

const LOG = Logger.get('TCM-StringMultiSelectFilter')

let UID_GEN = 0

const SITUACAO_INEXISTENTE = -1 as SituacaoCadastral

export class TcmPresenterForSituacaoCadastral extends FilterPresenter<
    MultipleOptionStringKeywordFilterScope,
    TcmPresenterForFilterPane
> {
    // Constructor

    constructor(
        owner: TcmPresenterForFilterPane,
        scope: MultipleOptionStringKeywordFilterScope,
        valueProperty: ValueProperty<SituacaoCadastral>,
        onSearch?: () => Promise<KeywordOption[]>
    ) {
        super(owner, scope)
        this.valueProperty = valueProperty
        this.onSearch = onSearch
    }

    // :: Fields

    protected valueProperty: ValueProperty<SituacaoCadastral>
    protected onSearch?: () => Promise<KeywordOption[]>

    // :: Getters and Setters

    get owner() {
        return super.owner as TcmPresenterForFilterPane
    }

    protected override async bindEvents() {
        this.scope.onAddFilter = this.action(this.handleAddFilter)
        this.scope.onClearAllFilters = this.action(this.handleClearAllFilters)
        LOG.debug('initialized')
    }

    override clear() {
        this.scope.conjunction.length = 0
        this.scope.disjunction.length = 0
        this.valueProperty.clear()
    }

    override publish() {
        this.valueProperty.clear()

        const emptyPredicate = (scope: MultipleOptionStringKeywordParcelFilterScope) =>
            scope.value.length === 0 && !isBlankKeywordOperator(scope.operator)
        this.scope.conjunction.removeByCriteria(emptyPredicate)
        this.scope.disjunction.removeByCriteria(emptyPredicate)

        const conjunction = [...this.scope.conjunction]
        const disjunction = [...this.scope.disjunction]
        if (conjunction.length === 0 && disjunction.length === 0) {
            return
        }

        const ALL = Object.values(SituacaoCadastral).filter((v): v is SituacaoCadastral => typeof v === 'number')

        const accepted = (parcelScope: MultipleOptionStringKeywordParcelFilterScope) => {
            const selected = new Set(parcelScope.value.map((s) => Number(s)))
            switch (parcelScope.operator) {
                case StringKeywordOperator.is_blank:
                    return new Set<SituacaoCadastral>() // toda empresa tem situação cadastral
                case StringKeywordOperator.is_not_blank:
                    return new Set(ALL)
                case StringKeywordOperator.is_not_in:
                    return new Set(ALL.filter((cd) => !selected.has(cd)))
                default:
                    return new Set(ALL.filter((cd) => selected.has(cd)))
            }
        }

        let result = new Set<SituacaoCadastral>(ALL)
        for (const parcelScope of conjunction) {
            const set = accepted(parcelScope)
            result = new Set([...result].filter((cd) => set.has(cd)))
        }

        if (disjunction.length > 0) {
            const union = new Set<SituacaoCadastral>()
            disjunction.forEach((parcelScope) => accepted(parcelScope).forEach((cd) => union.add(cd)))
            result = new Set([...result].filter((cd) => union.has(cd)))
        }

        this.valueProperty.apply(result.size > 0 ? [...result] : [SITUACAO_INEXISTENTE])
    }

    addConjuntion() {
        const andScope = new MultipleOptionStringKeywordParcelFilterScope()
        andScope.uid = `and-${UID_GEN++}`
        andScope.junctionMode = JunctionMode.AND
        andScope.onOperatorChange = this.action(this.handleOperatorChange.bind(this, andScope))
        andScope.onClear = this.action(this.handleClear.bind(this, andScope))
        andScope.onOptionSelected = this.action(this.handleOptionSelected.bind(this, andScope))
        andScope.getValueInOptionType = this.getValueInOptionType.bind(this, andScope)
        andScope.update = this.update
        this.doSearch(andScope)
        this.scope.conjunction.push(andScope)
        return andScope
    }

    addDisjuntion() {
        const orScope = new MultipleOptionStringKeywordParcelFilterScope()
        orScope.uid = `or-${UID_GEN++}`
        orScope.junctionMode = JunctionMode.OR
        orScope.onOperatorChange = this.action(this.handleOperatorChange.bind(this, orScope))
        orScope.onClear = this.action(this.handleClear.bind(this, orScope))
        orScope.onOptionSelected = this.action(this.handleOptionSelected.bind(this, orScope))
        orScope.getValueInOptionType = this.getValueInOptionType.bind(this, orScope)
        orScope.update = this.update
        this.doSearch(orScope)
        this.scope.disjunction.push(orScope)
        return orScope
    }

    getsIn() {
        for (const conditionScope of this.scope.conjunction) {
            if (conditionScope.operator === StringKeywordOperator.is_in) {
                return conditionScope
            }
        }

        for (const conditionScope of this.scope.disjunction) {
            if (conditionScope.operator === StringKeywordOperator.is_in) {
                return conditionScope
            }
        }

        return null
    }

    getOrCreateIsIn() {
        let conditioScope = this.getsIn()
        if (conditioScope) {
            return conditioScope
        }

        conditioScope = this.addConjuntion()
        conditioScope.operator = StringKeywordOperator.is_in
        return conditioScope
    }

    private async handleClearAllFilters() {
        this.clear()
    }

    private async handleAddFilter(mode: JunctionMode) {
        if (mode === JunctionMode.AND) {
            this.addConjuntion()
        } else {
            this.addDisjuntion()
        }
    }

    private async handleClear(parcelScope: MultipleOptionStringKeywordParcelFilterScope) {
        let idx = this.scope.conjunction.findIndex((item) => item.uid === parcelScope.uid)
        if (idx !== -1) {
            this.scope.conjunction.removeByIndex(idx)
        }

        idx = this.scope.disjunction.findIndex((item) => item.uid === parcelScope.uid)
        if (idx !== -1) {
            this.scope.disjunction.removeByIndex(idx)
        }
    }

    private async handleOperatorChange(
        parcelScope: MultipleOptionStringKeywordParcelFilterScope,
        value: StringKeywordOperator
    ) {
        parcelScope.operator = value
        parcelScope.valueVisible = !isBlankKeywordOperator(value)
    }

    private doSearch(parcelScope: MultipleOptionStringKeywordParcelFilterScope) {
        if (!this.onSearch) {
            parcelScope.options.length > 0 && (parcelScope.options = [])
            return
        }

        this.onSearch()
            .then((labels) => {
                parcelScope.options = labels
            })
            .catch((e) => {
                parcelScope.options = []
                LOG.error(e.message)
            })
        return
    }

    private getValueInOptionType(parcelScope: MultipleOptionStringKeywordParcelFilterScope): KeywordOption[] {
        const list = parcelScope.value
            .map((key) => parcelScope.options.find((opt) => opt.key === key))
            .filter((o) => !!o)
        return list as KeywordOption[]
    }

    private async handleOptionSelected(
        parcelScope: MultipleOptionStringKeywordParcelFilterScope,
        value: KeywordOption[]
    ) {
        parcelScope.value = value.map((v) => v.key)
    }

    apply(filters: SituacaoCadastral[] | null | undefined) {
        if (!filters || filters.length === 0) {
            return
        }

        filters.forEach((filter) => {
            if (!filter || filter === SITUACAO_INEXISTENTE) {
                return
            }

            const itemScope = this.addDisjuntion()

            itemScope.value = [`${filter}`]
            itemScope.operator = StringKeywordOperator.is_in
        })
    }
}
